import React, { useState, useEffect } from 'react';
import useSupportStore from '../stores/useSupportStore';
import FAQItem from '../components/FAQItem';
import LMSHeader from '../components/LMSHeader';

const HelpSupportPage = () => {
  const { faq, fetchFAQ, isLoading, error } = useSupportStore();
  const [iframeLoaded, setIframeLoaded] = useState(false);
  const [iframeLoadedTwo, setIframeLoadedTwo] = useState(false);
  const [activeTab, setActiveTab] = useState('faq');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [openFAQ, setOpenFAQ] = useState(null);

  const chatN = `${process.env.REACT_APP_PUBLIC_TAWK_PROPERTY_ID_CHAT}`;

  const KnwoN = `${process.env.REACT_APP_PUBLIC_TAWK_PROPERTY_ID_KWON}`;

  const handleToggle = (faqId) => {
    setOpenFAQ(openFAQ === faqId ? null : faqId);
  };

  useEffect(() => {
    if (activeTab === 'faq') {
      fetchFAQ(selectedCategory);
    }
  }, [activeTab, selectedCategory]);

  const filteredFaqs = faq?.filter(faq =>
    (selectedCategory === 'all' || faq.category === selectedCategory) &&
    (faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (isLoading && !faq?.length) {
    return (
      <>
        <LMSHeader />
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
        </div>
      </>
    );
  }

  if (error) {
    return (
      <>
        <LMSHeader />
        <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 flex items-center justify-center">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">Error loading data</h2>
            <p className="text-neutral-600 dark:text-neutral-400 mb-4">{error}</p>
            <button
              onClick={() => fetchFAQ(selectedCategory)}
              className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-medium rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Retry
            </button>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <LMSHeader />
      <div className="min-h-screen bg-neutral-50 dark:bg-neutral-900 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-neutral-900 dark:text-white mb-2">
              Help & Support
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Find answers to common questions or contact our support team
            </p>
          </div>

          {/* Tabs */}
          <div className="mb-8">
            <div className="border-b border-neutral-200 dark:border-neutral-700">
              <nav className="-mb-px flex space-x-8">
                {['faq', 'knowledge-base', 'live-support'].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`py-4 px-1 border-b-2 font-medium text-sm capitalize ${activeTab === tab
                      ? 'border-primary-500 text-primary-600 dark:text-primary-400'
                      : 'border-transparent text-neutral-500 dark:text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-300 hover:border-neutral-300 dark:hover:border-neutral-600'
                      }`}
                  >
                    {tab.replace('-', ' ')}
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* FAQ Tab */}
          {activeTab === 'faq' && (
            <div className="space-y-8">
              {/* Search and Filters */}
              <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6 mb-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label htmlFor="search" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Search
                    </label>
                    <input
                      type="text"
                      id="search"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="Search FAQs..."
                      className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white placeholder-neutral-500 dark:placeholder-neutral-400 focus:outline-none focus:ring-2 focus:ring-primary-500"
                    />
                  </div>
                  <div>
                    <label htmlFor="category" className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
                      Category
                    </label>
                    <select
                      id="category"
                      value={selectedCategory}
                      onChange={(e) => setSelectedCategory(e.target.value)}
                      className="w-full px-4 py-3 border border-neutral-300 dark:border-neutral-600 rounded-lg shadow-sm bg-white dark:bg-neutral-700 text-neutral-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    >
                      <option value="all">All Categories</option>
                      <option value="courses">Courses</option>
                      <option value="payments">Payments</option>
                      <option value="technical">Technical</option>
                      <option value="account">Account</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* FAQ List */}
              <div className="space-y-4">
                {filteredFaqs?.length > 0 ? (
                  filteredFaqs.map((faq) => (
                    <FAQItem
                      key={faq._id}
                      question={faq.question}
                      answer={faq.answer}
                      isOpen={openFAQ === faq._id}
                      onToggle={() => handleToggle(faq._id)}
                    />
                  ))
                ) : (
                  <div className="text-center py-12">
                    <div className="text-6xl mb-4">🔍</div>
                    <h3 className="text-xl font-semibold text-neutral-900 dark:text-white mb-2">
                      No FAQs found
                    </h3>
                    <p className="text-neutral-600 dark:text-neutral-400">
                      Try adjusting your search or filter criteria
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Tawk.to Knowledge Base */}
          {activeTab === 'knowledge-base' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">
                Knowledge Base
              </h2>

              <div className="relative flex items-center justify-center">

                {!iframeLoadedTwo && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white dark:bg-neutral-800 z-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mb-3"></div>
                    <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                      Loading KnowledgeBase...
                    </p>
                  </div>
                )}

                <iframe
                  src={KnwoN}
                  title="Knowledge Base"
                  width="100%"
                  height="600"
                  style={{ border: 'none' }}
                  allow="microphone; fullscreen"
                  onLoad={() => setIframeLoadedTwo(true)}
                />
              </div>
            </div>
          )}

          {/* Tawk.to Ticket Chat */}
          {activeTab === 'live-support' && (
            <div className="bg-white dark:bg-neutral-800 rounded-lg shadow-sm border border-neutral-200 dark:border-neutral-700 p-6">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-white mb-6">
                Chat With Support
              </h2>
              <div className="relative flex items-center justify-center">

                {!iframeLoaded && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center bg-white dark:bg-neutral-800 z-10">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-500 mb-3"></div>
                    <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                      Loading chat support...
                    </p>
                  </div>
                )}

                <iframe
                  src={chatN}
                  title="Live Support"
                  width="100%"
                  height="600"
                  style={{ border: 'none' }}
                  allow="microphone; fullscreen"
                  onLoad={() => setIframeLoaded(true)}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default HelpSupportPage;